import dayjs from 'dayjs'
import { Injectable } from '@tanbo/vue-di-plugin'
import { Http } from '@/services/http'
import { getTimeRangeByEnum } from '@/utils/time'
import { TimeGranularity, TimeRange } from '@/views/dashboard/_components/time-range-select/time-range.enum'
import type {
  DistributionResponseData,
  RiskTrendRequestParams,
  RiskTrendResponseData,
} from '@/views/dashboard/overview/service'

/** 告警趋势数据 */
export interface AlertTrendData {
  /** 时间点 */
  time: string
  /** 发现勒索风险终端数 */
  ransomware_risk: number
  /** 发现病毒风险终端数 */
  virus_risk: number
  /** 发现漏洞风险终端数 */
  vulnerability_risk: number
  /** 发现违规基线终端数 */
  baseline_violation: number
  /** 发现风险端口终端数 */
  port_risk: number
}

/** 告警趋势接口响应 */
export interface AlertTrendResponse {
  /** 告警趋势数据列表 */
  data: AlertTrendData[]
}

/** 威胁类型分布 */
export interface ThreatTypeDistribution {
  /** 威胁类型名称 */
  type: string
  /** 威胁数量 */
  count: number
  /** 占比百分比 */
  percentage: number
}

/** 威胁类型分布接口响应 */
export interface ThreatTypeDistributionResponse {
  /** 威胁类型分布数据列表 */
  data: ThreatTypeDistribution[]
}

function buildRiskTrendParams (timeRange: string): RiskTrendRequestParams {
  const [start, end] = getTimeRangeByEnum(timeRange as TimeRange)
  const endSec = end ?? Math.floor(Date.now() / 1000)
  const startSec =
    start ?? Math.floor(dayjs().subtract(365, 'day').valueOf() / 1000)
  const granularity =
    timeRange === TimeRange.ALL
      ? 'day'
      : (TimeGranularity[timeRange as keyof typeof TimeGranularity] ?? 'hour')
  return {
    start_time: String(startSec),
    end_time: String(endSec),
    granularity,
  }
}

function unwrapRiskTrendBody (res: RiskTrendResponseData & { data?: RiskTrendResponseData }): RiskTrendResponseData {
  return res.data ?? res
}

/** 与 `getAlertTrend` 共用：请求体为 start_time / end_time / granularity */
function buildAlertTrendRequestBody (params: {
  time_range?: string
  start_time?: string
  end_time?: string
}): RiskTrendRequestParams {
  const tr = params.time_range || '30d'
  const hasExplicitRange =
    params.start_time != null &&
    params.end_time != null &&
    params.start_time !== '' &&
    params.end_time !== ''
  if (hasExplicitRange) {
    return {
      start_time: String(params.start_time),
      end_time: String(params.end_time),
      granularity:
        tr === TimeRange.ALL
          ? 'day'
          : (TimeGranularity[tr as keyof typeof TimeGranularity] ?? 'hour'),
    }
  }
  return buildRiskTrendParams(tr)
}

function formatRiskTrendAxisLabel (ts: string, timeRange: string): string {
  const sec = Number(ts)
  if (Number.isNaN(sec)) return String(ts)
  if (timeRange === TimeRange.HOURS_24) {
    return dayjs.unix(sec).format('MM-DD HH:mm')
  }
  return dayjs.unix(sec).format('MM-DD')
}

function mapRiskTrendToAlertData (
  body: RiskTrendResponseData,
  timeRange: string,
): AlertTrendData[] {
  const tp = body.time_points || []
  const baseline = body.baseline_risk_counts || []
  const leak = body.leak_risk_counts || []
  const port = body.port_risk_counts || []
  const ransom = body.ransomware_risk_counts || []
  const virus = body.virus_risk_counts || []
  const n = tp.length
  const out: AlertTrendData[] = []
  for (let i = 0; i < n; i++) {
    out.push({
      time: formatRiskTrendAxisLabel(String(tp[i]), timeRange),
      ransomware_risk: ransom[i] ?? 0,
      virus_risk: virus[i] ?? 0,
      vulnerability_risk: leak[i] ?? 0,
      baseline_violation: baseline[i] ?? 0,
      port_risk: port[i] ?? 0,
    })
  }
  return out
}

function unwrapDistributionBody (res: DistributionResponseData & { data?: DistributionResponseData }): DistributionResponseData {
  return res.data ?? res
}

function mapVirusDistributionToThreat (body: DistributionResponseData): ThreatTypeDistribution[] {
  const types = body.risk_types || []
  const counts = body.counts || []
  const total = counts.reduce((a, b) => a + b, 0)
  return types.map((type, i) => {
    const count = counts[i] ?? 0
    return {
      type,
      count,
      percentage: total > 0 ? Math.round((count / total) * 10000) / 100 : 0,
    }
  })
}

@Injectable()
export class RealtimeMonitoringService {
  constructor (private http: Http) {}

  /** 告警综合趋势 — 终端维度（对接 `/terminal/risk-trend`） */
  getAlertTrend (params: {
    time_range?: string
    start_time?: string
    end_time?: string
  }) {
    const tr = params.time_range || '30d'
    const body = buildAlertTrendRequestBody(params)
    return this.http
      .post<RiskTrendResponseData & { data?: RiskTrendResponseData }>('/terminal/risk-trend', body)
      .then(res => ({
        data: mapRiskTrendToAlertData(unwrapRiskTrendBody(res), tr),
      }))
  }

  /** 风险事件趋势 — 风险维度（对接 `/terminal/risk-event-trend`，与 `HomeService.getTerminalRiskEventTrend` 同路径） */
  getTerminalRiskEventTrend (params: {
    time_range?: string
    start_time?: string
    end_time?: string
  }) {
    const tr = params.time_range || '30d'
    const body = buildAlertTrendRequestBody(params)
    return this.http
      .post<RiskTrendResponseData & { data?: RiskTrendResponseData }>('/terminal/risk-event-trend', body)
      .then(res => ({
        data: mapRiskTrendToAlertData(unwrapRiskTrendBody(res), tr),
      }))
  }

  /** 病毒类型分布（对接 /safemonitor/virus-scan/distribution，当前与时间段无关） */
  getThreatTypeDistribution (_params: { time_range?: string }) {
    return this.http
      .post<DistributionResponseData & { data?: DistributionResponseData }>('/safemonitor/virus-scan/distribution', {})
      .then(res => ({
        data: mapVirusDistributionToThreat(unwrapDistributionBody(res)),
      }))
  }

}

